import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import MapCardHeader from '@rsos/components/capstone/common/Map/MapCardHeader';
import {
  StyledPopover,
  StyledPinnedPopover,
  StyledPopoverCaret,
} from '@rsos/components/capstone/common/Map/useDeckGL/MapPopover/MapPopover.styles';
import { createWebMercatorViewport } from '@rsos/components/capstone/common/Map/useDeckGL/utils';
import {
  GEOCOMM,
  WORLD,
} from '@rsos/reduxDataStore/src/features/gisSearch/constants';
import PinnedMapPopoverHeader from './PinnedMapPopoverHeader';

const MemoizedPopover = React.memo(({ x, y, spatialLayer, spatialObject }) => {
  // Note: this is brittle, as the `attributes` can be provisioned differently
  // between layers. Agreement between product and devs is that static layers
  // have a `display_schema` property rather than `attributes` being the schema
  // like for dynamic layers
  const displaySchema = spatialObject.is_dynamic
    ? spatialLayer?.attributes
    : spatialLayer.attributes.display_schema;
  let timestamp =
    spatialObject?.date_created || spatialObject?.effective_date || 0;
  // For snapshots (e.g Axon), each individual object might have its own
  // timestamp for  when its data was last updated (date_pulled).
  if (spatialObject?.attributes?.date_pulled) {
    timestamp = spatialObject.attributes.date_pulled;
  }

  const coord = {
    x: x || 0,
    y: y || 0,
  };

  const fallbackTitle = spatialObject.name || spatialLayer.title;

  return (
    <StyledPopover x={coord.x} y={coord.y}>
      {spatialObject.attributes && spatialLayer && timestamp && (
        <MapCardHeader
          displaySchema={displaySchema}
          data={spatialObject.attributes}
          layer={spatialLayer}
          timestamp={spatialObject.is_dynamic ? timestamp : null}
          title={fallbackTitle}
          wrapHeader={false}
        />
      )}
      <StyledPopoverCaret />
    </StyledPopover>
  );
});
const MemoizedPinnedPopover = React.memo(({ x, y, location }) => {
  const isGeocomm = useSelector(state =>
    state.gisServices?.geocodeService?.gis_service_url?.includes(GEOCOMM),
  );
  const coord = {
    x: x || 0,
    y: y || 0,
  };
  /**
   * @constant {Boolean} isGlobalFormat - the check is to determine the UI's output for local geocoder
   * As of right now we have Geocomm and Non-Geocomm (DatamarkGis)
   * Geocomm's local gis response is different than ArcGis's default response
   * Where as Non-Geocomm is/will be following ArcGis's default format
   */
  const isGlobalFormat = useMemo(() => {
    if (isGeocomm) {
      return location.attributes.Loc_name === WORLD;
    }
    return true;
  }, [location, isGeocomm]);
  const isGlobalSource = useMemo(() => {
    return location.attributes.Loc_name === WORLD;
  }, [location]);

  const getPopoverHeight = () => {
    const { PlaceName, StAddr, SubAddr, RegionAbbr, Region } =
      location?.attributes ?? {};
    const fieldCounts = Object.values({ PlaceName, StAddr, SubAddr }).filter(
      v => v,
    ).length;
    const regionCounts = Object.values({
      RegionAbbr,
      Region,
    }).filter(v => v).length;
    const height = 96; // default with 1 address field and region

    if (!regionCounts) {
      // region is excluded when searching countries/oceans
      return 80; // single field
    }
    if (fieldCounts > 1) {
      const countsHeight = (fieldCounts - 1) * 14;
      return height + countsHeight;
    }
    return height;
  };
  const POPOVER_HEIGHT = useMemo(getPopoverHeight, [location]);
  return (
    <StyledPinnedPopover x={coord.x} y={coord.y} height={POPOVER_HEIGHT}>
      {Object.keys(location).length && (
        <PinnedMapPopoverHeader
          location={location}
          isGlobalFormat={isGlobalFormat}
          isGlobalSource={isGlobalSource}
        />
      )}
      <StyledPopoverCaret />
    </StyledPinnedPopover>
  );
});

const MapPopover = ({ overlay }) => {
  const [position, setPosition] = useState(null);
  const [pinnedPosition, setPinnedPosition] = useState(null);

  const popover = useSelector(state => state.mapPopover.popover);
  const pinnedPopover = useSelector(state => state.mapPopover.pinnedPopover);
  const basemap = useSelector(state => state.sinatra.ui.basemap);

  /**
   * Repositions the popover based on the current viewState.
   * @param {Object} viewState
   */
  const reposition = useCallback(
    (viewState, popover, pinnedPopover) => {
      // Use helper function that handles satellite mode adjustments
      const viewport = createWebMercatorViewport(viewState, basemap);
      if (popover) {
        const hoverPosition = popover?.spatialObject;
        if (hoverPosition) {
          let mostRecentPosition = [];

          if (hoverPosition.allCoordinates) {
            const coordinate = hoverPosition.allCoordinates[0];
            mostRecentPosition = [coordinate.lng, coordinate.lat];
          } else if (hoverPosition.center) {
            mostRecentPosition = hoverPosition.center.coordinates;
          } else {
            mostRecentPosition =
              hoverPosition.shape.features[0].geometry.coordinates;
          }
          const [x, y] = viewport.project(mostRecentPosition);
          setPosition({ x, y });
        }
      }

      if (pinnedPopover) {
        const hoverPinnedPosition = pinnedPopover?.location;
        try {
          const [x, y] = viewport.project([
            hoverPinnedPosition.x,
            hoverPinnedPosition.y,
          ]);
          const viewportHeight = viewport.height + viewport.fovy; // fovy: Field of view covered by camera
          const viewportWidth = viewport.width;
          // hide pinnedPopover when it moves it out of view
          if (y > viewportHeight || x > viewportWidth || x < 0 || y < 0) {
            setPinnedPosition(null);
          } else {
            setPinnedPosition({ x, y });
          }
        } catch {
          /* suppress, fail gracefully */
        }
      }
    },
    [setPosition, setPinnedPosition, basemap],
  );

  // Watch for viewState changes.
  useEffect(() => {
    if (overlay) {
      overlay.onViewStateChange(viewState => {
        reposition(viewState, popover, pinnedPopover);
      });
    }
  }, [overlay, popover, pinnedPopover, reposition]);

  // Reposition whenever the popover data changes.
  useEffect(() => {
    if (overlay && (popover || pinnedPopover)) {
      const viewState = overlay.getViewState();
      reposition(viewState, popover, pinnedPopover);
    }
    if (!popover) {
      setPosition(null);
    }
    if (!pinnedPopover) {
      setPinnedPosition(null);
    }
  }, [overlay, popover, pinnedPopover, reposition]);

  return (
    <>
      {position && popover && (
        <MemoizedPopover
          x={position?.x}
          y={position?.y}
          spatialLayer={popover?.spatialLayer}
          spatialObject={popover?.spatialObject}
        />
      )}
      {pinnedPosition && pinnedPopover && (
        <MemoizedPinnedPopover
          x={pinnedPosition?.x}
          y={pinnedPosition?.y}
          location={pinnedPopover}
        />
      )}
    </>
  );
};

export default MapPopover;
